import { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import { Joyride, EVENTS, STATUS, type EventData, type TooltipRenderProps } from "react-joyride";

import { AppStateContext } from "@/state/AppStateContext";
import { doTrackEvent } from "@/utils/utils";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const SPOTLIGHT_PADDING = 2;

// Mirrors the Settings dialog's control language (see Settings.tsx)
const focusRing = "focus-visible:ring-2 focus-visible:ring-primary/45 focus-visible:ring-offset-2 focus-visible:ring-offset-surface";
const buttonBase = `inline-flex h-9 shrink-0 items-center justify-center gap-2 rounded-lg px-3.5 text-[13px] font-medium transition-colors ${focusRing}`;
const primaryButton = `${buttonBase} bg-primary text-white shadow-[0_1px_2px_rgba(0,0,0,0.14)] hover:bg-primary/90`;
const neutralButton = `${buttonBase} border border-line-strong bg-surface text-ink hover:bg-hover`;
const ghostButton = `${buttonBase} px-2.5 text-ink-2 hover:bg-hover hover:text-ink`;

const TourTooltip = ({ index, size, step, isLastStep, backProps, primaryProps, skipProps, tooltipProps }: TooltipRenderProps) => (
  <div {...tooltipProps} className="w-[22rem] max-w-[calc(100vw-2rem)] rounded-2xl bg-surface p-5 text-start shadow-elevated">
    {step.title && <h2 className="mb-1.5 text-[15px] font-semibold tracking-tight text-ink">{step.title}</h2>}
    <div className="text-[13px] leading-relaxed text-ink-2 [&_strong]:font-medium [&_strong]:text-ink [&_ul]:mt-2 [&_ul]:space-y-1">{step.content}</div>
    <div className="mt-5 flex items-center gap-2">
      <span className="text-[11px] tabular-nums text-ink-3">{index + 1} of {size}</span>
      <div className="ms-auto flex items-center gap-2">
        {!isLastStep && <button type="button" {...skipProps} className={ghostButton}>Skip</button>}
        {index > 0 && <button type="button" {...backProps} className={neutralButton}>Back</button>}
        <button type="button" {...primaryProps} className={primaryButton}>{isLastStep ? 'Finish' : 'Next'}</button>
      </div>
    </div>
  </div>
);

const CustomJoyride = () => {
  const appStateContext = useContext(AppStateContext);
  const navigate = useNavigate();
  const FF_JOYRIDE = appStateContext?.state.frontendSettings?.feature_flags?.joyride || 0;
  const handleTrackEvent = (type: string, item: string, text: string, conversation_id: string, cgpt_id: string, agent_id: string) => doTrackEvent(type, item, text, conversation_id, cgpt_id, agent_id);
  const [stepIndex, setStepIndex] = useState<number>(0);
  const [run, setRun] = useState<boolean>(false);
  const [showPrompt, setShowPrompt] = useState<boolean>(false);
  const startAfterClose = useRef<boolean>(false);
  const [spotlightRect, setSpotlightRect] = useState<DOMRect | null>(null);

  /**
   * Setup tutorial joyride
  **/
  const joyrideComplete = localStorage.getItem('sms-gpt_joyrideComplete')
  useEffect(() => {
    // The tour targets desktop layout; skip it on mobile
    if (window.matchMedia('(max-width: 700px)').matches) return;
    if (FF_JOYRIDE == 1 && joyrideComplete !== '1') setShowPrompt(true);
  }, [FF_JOYRIDE, joyrideComplete]);

  // Close the prompt first; the tour starts once the dialog has fully unmounted (see onCloseAutoFocus)
  const handleStartTour = () => {
    navigate('/');
    startAfterClose.current = true;
    setShowPrompt(false);
  };

  // Radix focuses the first button on open, which shows its focus ring; focus the dialog itself instead
  // so the focus trap and Esc still work but no button looks pre-selected
  const handlePromptOpened = (e: Event) => {
    e.preventDefault();
    (e.currentTarget as HTMLElement).focus();
  };

  const handlePromptClosed = (e: Event) => {
    e.preventDefault(); // Don't restore focus, which can scroll the page
    if (!startAfterClose.current) return;
    startAfterClose.current = false;
    setStepIndex(0);
    setRun(true);
  };

  // The tooltip briefly overflows while it positions itself, flashing a page scrollbar between steps
  useEffect(() => {
    if (!run) return;
    const html = document.documentElement;
    const prevOverflow = html.style.overflow;
    html.style.overflow = 'hidden';
    return () => { html.style.overflow = prevOverflow; };
  }, [run]);

  // Dismiss for this page load only; the prompt returns on next reload
  const handleLater = () => setShowPrompt(false);

  const steps = [
    {
      target: '.tour-step-1',
      content: 'Show or hide the sidebar to give your chat more room.',
      skipBeacon: true,
    },
    {
      target: '.tour-step-2',
      content: 'Search your past conversations.',
    },
    {
      target: '.tour-step-3',
      content: 'Start a new conversation with ALLY.',
    },
    {
      target: '.tour-step-4',
      content: (<div><strong>Spaces</strong> (formerly Custom GPTs) are assistants set up for a specific task, topic, or area of expertise.</div>),
    },
    {
      target: '.tour-step-5',
      content: (<div><strong>Agents</strong> combine several Spaces to handle more complex, multi-step tasks.</div>),
    },
    {
      target: '.tour-step-6',
      content: (<div><strong>Library</strong> holds every file you've uploaded and every file ALLY has created for you, all in one place.</div>),
    },
    {
      target: '.tour-step-7',
      content: "Browse AI-generated images you're free to reuse. Always label them as AI-generated when you share them.",
    },
    {
      target: '.tour-step-8',
      content: 'Work on a topic together with colleagues and ALLY in a group chat.',
    },
    {
      target: '.tour-step-9',
      content: 'Your previous chats are saved here, so you can return to them anytime.',
    },
    {
      target: '.tour-step-10',
      content: (
        <div>
          Ask a question or describe the task you need help with.
          <br/><br/>
          For tips on writing effective prompts, see the <a className="text-blue-500 underline" href="https://smsgroupgmbh.sharepoint.com/sites/intranet-content/SitePages/AI.aspx" target="_blank" rel="noopener noreferrer">Working with AI</a> page on the intranet.
        </div>
      )
    },
    {
      target: '.tour-step-11',
      content: 'Attach files or turn on tools to give ALLY more context.',
    },
    {
      target: '.tour-step-12',
      content: (
        <div>
          Choose how much effort ALLY puts into your request:
          <ul className="text-start ps-6">
            <li><strong>Instant:</strong> quick answers</li>
            <li><strong>Standard:</strong> everyday tasks</li>
            <li><strong>Complex:</strong> in-depth analysis and harder problems</li>
          </ul>
        </div>
      )
    },
    {
      target: '.tour-step-13',
      content: 'Dictate your message instead of typing it.',
    },
    {
      target: '.tour-step-14',
      content: 'Have a real-time voice conversation with ALLY. Speak naturally, ask follow-up questions, and hear the answers read aloud, just like talking to a colleague.',
    },
    {
      target: '.tour-step-15',
      content: 'Generate an image in a style and aspect ratio of your choice.',
    },
    {
      target: '.tour-step-16',
      content: 'Ask ALLY about your Microsoft 365 data, such as your calendar, emails, and Teams chats.',
    },
    {
      target: '.tour-step-17',
      content: 'Search the web when you need up-to-date information from public sources.',
    },
  ];

  // Keep the dim layer mounted between steps and glide the spotlight between targets.
  const spotlightTarget = steps[stepIndex]?.target;
  useEffect(() => {
    if (!run || !spotlightTarget) { setSpotlightRect(null); return; }
    const measure = () => {
      const el = document.querySelector(spotlightTarget);
      if (el) setSpotlightRect(el.getBoundingClientRect());
    };
    const frame = requestAnimationFrame(measure);
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [run, spotlightTarget]);

  const handleJoyrideEvent = (data: EventData) => {
    const { action, index, status, type } = data;

    // The v3 tour:end event carries the final status once the tour has ended.
    if (type === EVENTS.TOUR_END && (status === STATUS.FINISHED || status === STATUS.SKIPPED)) {
      setRun(false);
      setStepIndex(0);
      localStorage.setItem('sms-gpt_joyrideComplete', '1');

      if (status === STATUS.FINISHED) handleTrackEvent('click', 'joyride_complete', 'Joyride Complete', '', '', '');
      else handleTrackEvent('click', 'joyride_skipped', 'Joyride Skipped', '', '', '');
    } else if (type === EVENTS.STEP_AFTER) {
      // Move the controlled tour state after a step is completed.
      const nextStepIndex = index + (action === 'prev' ? -1 : 1);
      setStepIndex(nextStepIndex);
    }
  };

  return (
    <>
    <Dialog open={showPrompt} onOpenChange={(open) => { if (!open) handleLater(); }}>
      <DialogContent onOpenAutoFocus={handlePromptOpened} onCloseAutoFocus={handlePromptClosed} className="max-w-sm w-[calc(100%-2rem)] gap-0 rounded-3xl border-transparent bg-surface p-6 shadow-elevated [&>button]:hidden">
        <DialogHeader className="space-y-0 text-start sm:text-start">
          <DialogTitle className="text-[1.375rem] font-semibold leading-tight tracking-[-0.015em] text-ink">Welcome to ALLY Chat</DialogTitle>
          <DialogDescription className="mt-1.5 text-[13px] leading-relaxed text-ink-2">
            <div>Explore the key features in a quick guided tour.</div>
            <div className="mt-4">Read more about ALLY <a className="text-sky-500 underline" href="https://smsgroupgmbh.sharepoint.com/sites/intranet-news/SitePages/ALLY-is-now-available--Discover-the-new-features-in-ALLY-Chat.aspx" target="_blank" rel="noopener noreferrer">here</a>.</div>
          </DialogDescription>
        </DialogHeader>
        <div className="mt-6 flex items-center justify-end gap-2 border-t border-line pt-5">
          <button type="button" className={neutralButton} onClick={handleLater}>Later</button>
          <button type="button" className={primaryButton} onClick={handleStartTour}>Start tour</button>
        </div>
      </DialogContent>
    </Dialog>
    {run && createPortal(
      // Full-screen layer blocks clicks on the page; the spotlight's box-shadow provides the dimming
      <div className="fixed inset-0 z-[9999]" role="presentation">
        {spotlightRect &&
          <div
            className="fixed rounded-lg transition-all duration-300 ease-out"
            style={{
              top: spotlightRect.top - SPOTLIGHT_PADDING,
              left: spotlightRect.left - SPOTLIGHT_PADDING,
              width: spotlightRect.width + SPOTLIGHT_PADDING * 2,
              height: spotlightRect.height + SPOTLIGHT_PADDING * 2,
              boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.7)',
            }}
          />
        }
      </div>,
      document.body
    )}
    <Joyride
      steps={steps}
      run={run}
      stepIndex={stepIndex}
      continuous={true}
      options={{
        arrowColor: appStateContext?.state.theme === 'dark' ? '#181d26' : '#ffffff',
        buttons: ['back', 'skip', 'primary'],
        hideOverlay: true,
        overlayClickAction: false,
        skipScroll: true,
        spotlightPadding: SPOTLIGHT_PADDING,
        zIndex: 10000,
      }}
      onEvent={handleJoyrideEvent}
      tooltipComponent={TourTooltip}
      locale={{
        open: 'Take the tour!',
        back: 'Back',
        close: 'Close',
        last: 'Finish',
        next: 'Next',
        skip: 'Skip',
      }}
    />
    </>
  );
}

export default CustomJoyride;